import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  AccountBalance, 
  Assessment,
  Refresh,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Pending,
  Cancel
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { WebSocketContext } from '../WebSocketContext';
import config from '../config';
import './MasterDashboard.css';

const MasterDashboard = () => {
  const { isDarkMode } = useTheme();
  const { userDetails, stocks, trades, positions } = useContext(WebSocketContext);
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const [orders, setOrders] = useState([]);
  const [rejectionLogs, setRejectionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalClients: 0,
    activeClients: 0,
    totalTrades: 0,
    totalVolume: 0,
    totalPnL: 0,
    marginUsed: 0,
    marginAvailable: 0
  });

  // Fetch today's orders
  const fetchTodayOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const clientId = localStorage.getItem('userId');
      const role = localStorage.getItem('role');
      
      const response = await fetch(`${config.API_BASE_URL}getTodayOrders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: clientId,
          user_type: role === 'M' ? 'master' : 'broker'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      } else {
        console.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch today's rejection logs
  const fetchTodayRejectionLogs = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const clientId = localStorage.getItem('userId');
      const role = localStorage.getItem('role');
      
      const response = await fetch(`${config.API_BASE_URL}getTodayRejectionLog`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: clientId,
          user_type: role === 'M' ? 'master' : 'user'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setRejectionLogs(data);
      } else {
        console.error('Failed to fetch rejection logs');
      }
    } catch (error) {
      console.error('Error fetching rejection logs:', error);
    }
  };

  // Mock data - replace with actual API calls
  useEffect(() => {
    // Simulate API call
    setDashboardData({
      totalClients: 156,
      activeClients: 89,
      totalTrades: 1247,
      totalVolume: 12500000,
      totalPnL: 125000,
      marginUsed: 850000,
      marginAvailable: 150000
    });
    
    // Fetch orders and rejection logs
    fetchTodayOrders();
    fetchTodayRejectionLogs();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const getPnLColor = (pnl) => {
    if (pnl > 0) return '#4caf50';
    if (pnl < 0) return '#f44336';
    return '#666666';
  };

  // Helper functions for order processing
  const getOrderStatusIcon = (orderStatus) => {
    switch(orderStatus) {
      case 1: return <Pending style={{ color: '#ff9800' }} />;
      case 2:
      case 3: return <CheckCircle style={{ color: '#4caf50' }} />;
      default: return <Cancel style={{ color: '#f44336' }} />;
    }
  };

  const getOrderStatusText = (orderStatus) => {
    switch(orderStatus) {
      case 1: return 'Pending';
      case 2: return 'Executed';
      case 3: return 'Executed';
      default: return 'Rejected';
    }
  };

  const getOrderTypeText = (orderType) => {
    switch(orderType) {
      case '1' || 1: return 'Market';
      case '2' || 2: return 'Limit';
      case '3' || 3: return 'SL';
      default: return 'Unknown';
    }
  };

  const getBuySellText = (buySell) => {
    return buySell === '1' ? 'Buy' : 'Sell';
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Filter orders by status
  const executedOrders = orders.filter(order => order.orderStatus == 2 || order.orderStatus == 3);
  const pendingOrders = orders.filter(order => order.orderStatus == 1);
  // Use rejection logs from separate API instead of filtering orders
  const rejectedOrders = rejectionLogs;

  const StatCard = ({ title, value, icon, color, subtitle, sensitive = false }) => (
    <Card className={`master-stat-card ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" className="master-stat-title">
              {title}
            </Typography>
            <Typography variant="h4" className="master-stat-value" style={{ color }}>
              {sensitive && !showSensitiveData ? '••••••' : value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" className="master-stat-subtitle">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box className="master-stat-icon" style={{ color }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const QuickActionCard = ({ title, description, icon, onClick, color }) => (
    <Card 
      className={`master-action-card ${isDarkMode ? 'dark-theme' : 'light-theme'}`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Box className="master-action-icon" style={{ backgroundColor: color }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" className="master-action-title">
              {title}
            </Typography>
            <Typography variant="body2" className="master-action-description">
              {description}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const OrdersTable = ({ title, orders, showRejectionReason = false }) => (
    <Card className={`master-orders-card ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" className="master-section-title">
            {title}
          </Typography>
          <Link href="#" className="master-view-more-link">
            View More
          </Link>
        </Box>
        
        <TableContainer component={Paper} className="master-orders-table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className="master-table-header">D</TableCell>
                <TableCell className="master-table-header">TIME</TableCell>
                <TableCell className="master-table-header">CLIENT</TableCell>
                <TableCell className="master-table-header">SCRIPT</TableCell>
                <TableCell className="master-table-header">TYPE</TableCell>
                <TableCell className="master-table-header">LOT</TableCell>
                <TableCell className="master-table-header">QTY</TableCell>
                <TableCell className="master-table-header">RATE</TableCell>
                {showRejectionReason && (
                  <TableCell className="master-table-header">REASON</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.slice(0, 10).map((order, index) => (
                <TableRow key={index} className="master-table-row">
                  <TableCell className="master-table-cell">
                    {showRejectionReason ? <Cancel style={{ color: '#f44336' }} /> : getOrderStatusIcon(order.orderStatus)}
                  </TableCell>
                  <TableCell className="master-table-cell">
                    {showRejectionReason ? formatTime(order.dateTime) : formatTime(order.executionDateTime || order.orderDateTime)}
                  </TableCell>
                  <TableCell className="master-table-cell">
                    {showRejectionReason ? order.clientId : (order.clientName || `(${order.clientId})`)}
                  </TableCell>
                  <TableCell className="master-table-cell">
                    {showRejectionReason ? order.stockName : (order.stockName || order.instrumentName)}
                  </TableCell>
                  <TableCell className="master-table-cell">
                    {showRejectionReason ? 
                      `${getOrderTypeText(order.orderExecutionType)} ${getBuySellText(order.buy_sell)}` :
                      `${getOrderTypeText(order.orderType)} ${getBuySellText(order.buy_sell)}`
                    }
                  </TableCell>
                  <TableCell className="master-table-cell">
                    {showRejectionReason ? order.lot : (order.orderLotQuantity || order.lots || 0)}
                  </TableCell>
                  <TableCell className="master-table-cell">
                    {showRejectionReason ? order.quantity : (order.orderQuantity || order.quantity || 0)}
                  </TableCell>
                  <TableCell className="master-table-cell">
                    {showRejectionReason ? order.orderPrice : (order.orderPrice || order.price || 0)}
                  </TableCell>
                  {showRejectionReason && (
                    <TableCell className="master-table-cell">
                      {order.remark || 'N/A'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  return (
    <div className={`master-dashboard ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Main Content */}
      <div className="master-dashboard-content">
        {/* Desktop Layout */}
        <div className="master-desktop-layout">
          <div className="master-main-content">
            {/* Header */}
            <Box className="master-dashboard-header">
              <Box>
                <Typography variant="h4" className="master-dashboard-title">
                  Master Dashboard
                </Typography>
                <Typography variant="body1" className="master-dashboard-subtitle">
                  Welcome back, {userDetails?.clientName || 'Master'}
                </Typography>
              </Box>
              <Box display="flex" gap={2} alignItems="center">
                <IconButton
                  onClick={() => setShowSensitiveData(!showSensitiveData)}
                  className="master-visibility-toggle"
                >
                  {showSensitiveData ? <VisibilityOff /> : <Visibility />}
                </IconButton>
                <IconButton className="master-refresh-btn" onClick={() => {
                  fetchTodayOrders();
                  fetchTodayRejectionLogs();
                }}>
                  <Refresh />
                </IconButton>
              </Box>
            </Box>

            {/* Stats Grid */}
            <Grid container spacing={3} className="master-stats-grid">
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Clients"
                  value={formatNumber(dashboardData.totalClients)}
                  icon={<AccountBalance />}
                  color="#2196f3"
                  subtitle={`${dashboardData.activeClients} active`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Trades"
                  value={formatNumber(dashboardData.totalTrades)}
                  icon={<Assessment />}
                  color="#ff9800"
                  subtitle="Today"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Volume"
                  value={formatCurrency(dashboardData.totalVolume)}
                  icon={<TrendingUp />}
                  color="#9c27b0"
                  sensitive={true}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total P&L"
                  value={formatCurrency(dashboardData.totalPnL)}
                  icon={<TrendingDown />}
                  color={getPnLColor(dashboardData.totalPnL)}
                  sensitive={true}
                />
              </Grid>
            </Grid>

            {/* Quick Actions */}
            <Box className="master-quick-actions">
              <Typography variant="h5" className="master-section-title">
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <QuickActionCard
                    title="View All Clients"
                    description="Manage client accounts and settings"
                    icon={<AccountBalance />}
                    color="#2196f3"
                    onClick={() => {/* Navigate to clients */}}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <QuickActionCard
                    title="Trade Analysis"
                    description="Analyze trading patterns and performance"
                    icon={<Assessment />}
                    color="#ff9800"
                    onClick={() => {/* Navigate to analysis */}}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <QuickActionCard
                    title="Portfolio Overview"
                    description="View overall portfolio performance"
                    icon={<TrendingUp />}
                    color="#4caf50"
                    onClick={() => {/* Navigate to portfolio */}}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Orders Tables */}
            <Box className="master-orders-section">
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <OrdersTable 
                    title="Executed Order" 
                    orders={executedOrders}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <OrdersTable 
                    title="Pending Order" 
                    orders={pendingOrders}
                  />
                </Grid>
                <Grid item xs={12}>
                  <OrdersTable 
                    title="Rejection Log" 
                    orders={rejectedOrders}
                    showRejectionReason={true}
                  />
                </Grid>
              </Grid>
            </Box>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="master-mobile-layout">
          <div className="master-mobile-content">
            {/* Mobile Header */}
            <Box className="master-mobile-header">
              <Typography variant="h5" className="master-mobile-title">
                Dashboard
              </Typography>
              <Box display="flex" gap={1}>
                <IconButton
                  onClick={() => setShowSensitiveData(!showSensitiveData)}
                  size="small"
                >
                  {showSensitiveData ? <VisibilityOff /> : <Visibility />}
                </IconButton>
                <IconButton size="small" onClick={() => {
                  fetchTodayOrders();
                  fetchTodayRejectionLogs();
                }}>
                  <Refresh />
                </IconButton>
              </Box>
            </Box>

            {/* Mobile Stats */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StatCard
                  title="Clients"
                  value={formatNumber(dashboardData.totalClients)}
                  icon={<AccountBalance />}
                  color="#2196f3"
                />
              </Grid>
              <Grid item xs={6}>
                <StatCard
                  title="Trades"
                  value={formatNumber(dashboardData.totalTrades)}
                  icon={<Assessment />}
                  color="#ff9800"
                />
              </Grid>
              <Grid item xs={6}>
                <StatCard
                  title="Volume"
                  value={formatCurrency(dashboardData.totalVolume)}
                  icon={<TrendingUp />}
                  color="#9c27b0"
                  sensitive={true}
                />
              </Grid>
              <Grid item xs={6}>
                <StatCard
                  title="P&L"
                  value={formatCurrency(dashboardData.totalPnL)}
                  icon={<TrendingDown />}
                  color={getPnLColor(dashboardData.totalPnL)}
                  sensitive={true}
                />
              </Grid>
            </Grid>

            {/* Mobile Orders Tables */}
            <Box className="master-mobile-orders">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <OrdersTable 
                    title="Executed Order" 
                    orders={executedOrders}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <OrdersTable 
                    title="Pending Order" 
                    orders={pendingOrders}
                  />
                </Grid>
                <Grid item xs={12}>
                  <OrdersTable 
                    title="Rejection Log" 
                    orders={rejectedOrders}
                    showRejectionReason={true}
                  />
                </Grid>
              </Grid>
            </Box>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterDashboard;
